import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, UserCircle, Clock, Edit2, X, Check, ChevronDown, Building2 } from 'lucide-react';
import { api } from '../lib/api';
import { formatDate } from '../lib/utils';
import { useNotification } from '../components/NotificationProvider';
import { JiraDropdown } from '../components/JiraDropdown';
import { ProcessStageDropdown } from '../components/ProcessStageDropdown';
import { OperationalTimesCard } from '../components/OperationalTimesCard';
import { useSetupConfig } from '../hooks/useSetupConfigs';

// Map process stage codes to display labels
const PROCESS_STAGE_LABELS: Record<string, string> = {
  open: 'Reported',
  false_alarm: 'False Alarm',
  in_progress: 'In Progress',
  on_site: 'On Site',
  in_analyzes: 'In Analyzes',
  decommissioning: 'Decommissioning',
  repair_begin: 'Repair Begin',
  waiting_parts: 'Waiting for Parts',
  repair_completed: 'Repair Completed',
  recommissioning: 'Recommissioning',
  recorded: 'Recorded',
  done: 'Done',
};

interface ConfigValue {
  id: string;
  label: string;
  value: string;
  enabled?: boolean;
}

interface RequestDetail {
  request: {
    id: string;
    ticketNumber: string;
    description: string;
    status: string;
    reportingType: string | null;
    location: string;
    branchName?: string;
    technicianName: string | null;
    responseTime: number | null;
    completionTime: number | null;
    leakageType: string | null;
    materialUsed: string | null;
    zones: string | null;
    technicalAllocation: string | null;
    damageType: string | null;
    repairMaterial: string | null;
    pipeDiameter: string | null;
    pipeMaterial: string | null;
    supplyPressure: string | null;
    holeSize: string | null;
    createdAt: string;
    updatedAt: string;
    completedAt: string | null;
    // Process stage & operational timestamps
    process_stage?: string;
    timestamp_open?: string | null;
    timestamp_false_alarm?: string | null;
    timestamp_in_progress?: string | null;
    timestamp_on_site?: string | null;
    timestamp_in_analyzes?: string | null;
    timestamp_decommission?: string | null;
    timestamp_repair_begin?: string | null;
    timestamp_waiting_parts?: string | null;
    timestamp_repair_done?: string | null;
    timestamp_recommission?: string | null;
    timestamp_recorded?: string | null;
    timestamp_done?: string | null;
  };
  updates: Array<{
    id: string;
    note: string;
    status: string;
    createdAt: string;
  }>;
  permissions: {
    canEdit: boolean;
    canAddComment: boolean;
  };
}

/**
 * Format duration in minutes with smart unit selection
 * < 60 min: shows minutes
 * 60 min - 24 hours: shows hours and minutes
 * >= 24 hours: shows only days (simplified for averages)
 */
function formatDuration(minutes: number | null): string {
  if (minutes === null) return '';

  // Less than 60 minutes - show minutes only
  if (minutes < 60) {
    return `${minutes}min`;
  }

  // Less than 1440 minutes (24 hours) - show hours and minutes
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}min`;
  }

  // 24 hours or more - show only days (simplified for averages)
  const days = Math.round(minutes / 1440);
  return `${days}d`;
}

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');

  // Form state for editable fields
  const [leakageType, setLeakageType] = useState('');
  const [materialUsed, setMaterialUsed] = useState('');
  const [reportingType, setReportingType] = useState('');
  const [zones, setZones] = useState('');
  const [technicalAllocation, setTechnicalAllocation] = useState('');
  const [damageType, setDamageType] = useState('');
  const [repairMaterial, setRepairMaterial] = useState('');
  const [pipeDiameter, setPipeDiameter] = useState('');
  const [pipeMaterial, setPipeMaterial] = useState('');
  const [supplyPressure, setSupplyPressure] = useState('');
  const [holeSize, setHoleSize] = useState('');

  // Use React Query hooks for configs to auto-invalidate cache
  const { data: reportingTypeConfig, isLoading: loadingReportingType } = useSetupConfig('reporting_type');
  const { data: zonesConfig, isLoading: loadingZones } = useSetupConfig('zones');
  const { data: technicalAllocationConfig, isLoading: loadingTechnicalAllocation } = useSetupConfig('technical_allocation');
  const { data: damageTypeConfig, isLoading: loadingDamageType } = useSetupConfig('damage_type');
  const { data: repairMaterialConfig, isLoading: loadingRepairMaterial } = useSetupConfig('repair_material');
  const { data: pipeDiameterConfig, isLoading: loadingPipeDiameter } = useSetupConfig('pipe_diameter');
  const { data: pipeMaterialConfig, isLoading: loadingPipeMaterial } = useSetupConfig('pipe_material');
  const { data: supplyPressureConfig, isLoading: loadingSupplyPressure } = useSetupConfig('supply_pressure');
  const { data: holeSizeConfig, isLoading: loadingHoleSize } = useSetupConfig('hole_size');

  // Derived options from configs
  const reportingTypeOptions = React.useMemo(() => {
    if (!reportingTypeConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (reportingTypeConfig.config.useDefaults && reportingTypeConfig.config.defaultValues) {
      enabledValues.push(...reportingTypeConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (reportingTypeConfig.config.customValues) {
      enabledValues.push(...reportingTypeConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [reportingTypeConfig]);

  const zonesOptions = React.useMemo(() => {
    if (!zonesConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (zonesConfig.config.useDefaults && zonesConfig.config.defaultValues) {
      enabledValues.push(...zonesConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (zonesConfig.config.customValues) {
      enabledValues.push(...zonesConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [zonesConfig]);

  const technicalAllocationOptions = React.useMemo(() => {
    if (!technicalAllocationConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (technicalAllocationConfig.config.useDefaults && technicalAllocationConfig.config.defaultValues) {
      enabledValues.push(...technicalAllocationConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (technicalAllocationConfig.config.customValues) {
      enabledValues.push(...technicalAllocationConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [technicalAllocationConfig]);

  const damageTypeOptions = React.useMemo(() => {
    if (!damageTypeConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (damageTypeConfig.config.useDefaults && damageTypeConfig.config.defaultValues) {
      enabledValues.push(...damageTypeConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (damageTypeConfig.config.customValues) {
      enabledValues.push(...damageTypeConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [damageTypeConfig]);

  const repairMaterialOptions = React.useMemo(() => {
    if (!repairMaterialConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (repairMaterialConfig.config.useDefaults && repairMaterialConfig.config.defaultValues) {
      enabledValues.push(...repairMaterialConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (repairMaterialConfig.config.customValues) {
      enabledValues.push(...repairMaterialConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [repairMaterialConfig]);

  const pipeDiameterOptions = React.useMemo(() => {
    if (!pipeDiameterConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (pipeDiameterConfig.config.useDefaults && pipeDiameterConfig.config.defaultValues) {
      enabledValues.push(...pipeDiameterConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (pipeDiameterConfig.config.customValues) {
      enabledValues.push(...pipeDiameterConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [pipeDiameterConfig]);

  const pipeMaterialOptions = React.useMemo(() => {
    if (!pipeMaterialConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (pipeMaterialConfig.config.useDefaults && pipeMaterialConfig.config.defaultValues) {
      enabledValues.push(...pipeMaterialConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (pipeMaterialConfig.config.customValues) {
      enabledValues.push(...pipeMaterialConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [pipeMaterialConfig]);

  const supplyPressureOptions = React.useMemo(() => {
    if (!supplyPressureConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (supplyPressureConfig.config.useDefaults && supplyPressureConfig.config.defaultValues) {
      enabledValues.push(...supplyPressureConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (supplyPressureConfig.config.customValues) {
      enabledValues.push(...supplyPressureConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [supplyPressureConfig]);

  const holeSizeOptions = React.useMemo(() => {
    if (!holeSizeConfig?.config) return [];
    const enabledValues: ConfigValue[] = [];
    if (holeSizeConfig.config.useDefaults && holeSizeConfig.config.defaultValues) {
      enabledValues.push(...holeSizeConfig.config.defaultValues.filter((v: any) => v.enabled !== false));
    }
    if (holeSizeConfig.config.customValues) {
      enabledValues.push(...holeSizeConfig.config.customValues.filter((v: any) => v.enabled));
    }
    return enabledValues;
  }, [holeSizeConfig]);

  const loadingConfigs = loadingReportingType || loadingZones || loadingTechnicalAllocation || loadingDamageType || loadingRepairMaterial || loadingPipeDiameter || loadingPipeMaterial || loadingSupplyPressure || loadingHoleSize;

  // State for comment
  const [comment, setComment] = useState('');

  // History tab state
  const [activeTab, setActiveTab] = useState<'comments' | 'history'>('comments');

  // Pagination state
  const [commentsToShow, setCommentsToShow] = useState(5);
  const [historyToShow, setHistoryToShow] = useState(5);

  const { showNotification } = useNotification();

  useEffect(() => {
    if (id) {
      fetchRequest();
    }
  }, [id]);


  const fetchRequest = async () => {
    try {
      const result = await api.get<RequestDetail>(`/requests/${id}`);
      console.log('📋 Full response:', result);
      console.log('📋 Permissions:', result.permissions);
      console.log('📋 Request fetched:', { canEdit: result?.permissions?.canEdit, roleFromRequest: result?.request });
      setData(result);
      setEditedDescription(result.request.description);
      setLeakageType(result.request.leakageType || '');
      setMaterialUsed(result.request.materialUsed || '');
      setReportingType(result.request.reportingType || '');
      setZones(result.request.zones || '');
      setTechnicalAllocation(result.request.technicalAllocation || '');
      setDamageType(result.request.damageType || '');
      setRepairMaterial(result.request.repairMaterial || '');
      setPipeDiameter(result.request.pipeDiameter || '');
      setPipeMaterial(result.request.pipeMaterial || '');
      setSupplyPressure(result.request.supplyPressure || '');
      setHoleSize(result.request.holeSize || '');
    } catch (error) {
      console.error('Failed to fetch request:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!data) return;

    setSaving(true);
    try {
      await api.put(`/requests/${id}`, {
        description: editedDescription,
      });
      await fetchRequest();
      setIsEditingDescription(false);
      showNotification('success', 'Description saved successfully');
    } catch (error: any) {
      console.error('Failed to save description:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);
      showNotification('error', error?.response?.data?.error || error?.response?.data?.message || 'Failed to save description');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDescription = () => {
    setEditedDescription(data?.request.description || '');
    setIsEditingDescription(false);
  };

  const handleSaveDetails = async () => {
    if (!data) return;

    setSaving(true);
    try {
      const updatedFields: any = {};

      // Only include fields that have changed
      if (leakageType !== (data.request.leakageType || '')) {
        updatedFields.leakageType = leakageType;
      }
      if (materialUsed !== (data.request.materialUsed || '')) {
        updatedFields.materialUsed = materialUsed;
      }
      if (reportingType !== (data.request.reportingType || '')) {
        updatedFields.reportingType = reportingType;
      }
      if (zones !== (data.request.zones || '')) {
        updatedFields.zones = zones;
      }
      if (technicalAllocation !== (data.request.technicalAllocation || '')) {
        updatedFields.technicalAllocation = technicalAllocation;
      }
      if (damageType !== (data.request.damageType || '')) {
        updatedFields.damageType = damageType;
      }
      if (repairMaterial !== (data.request.repairMaterial || '')) {
        updatedFields.repairMaterial = repairMaterial;
      }
      if (pipeDiameter !== (data.request.pipeDiameter || '')) {
        updatedFields.pipeDiameter = pipeDiameter;
      }
      if (pipeMaterial !== (data.request.pipeMaterial || '')) {
        updatedFields.pipeMaterial = pipeMaterial;
      }
      if (supplyPressure !== (data.request.supplyPressure || '')) {
        updatedFields.supplyPressure = supplyPressure;
      }
      if (holeSize !== (data.request.holeSize || '')) {
        updatedFields.holeSize = holeSize;
      }

      // Only make API call if there are changes
      if (Object.keys(updatedFields).length > 0) {
        await api.put(`/requests/${id}`, updatedFields);
        await fetchRequest();
        showNotification('success', 'Details saved successfully');
      }
    } catch (error: any) {
      console.error('Failed to save details:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);
      showNotification('error', error?.response?.data?.error || error?.response?.data?.message || 'Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!data || !comment.trim()) {
      showNotification('error', 'Please add a comment');
      return;
    }

    setUpdating(true);
    try {
      await api.put(`/requests/${id}/status`, {
        process_stage: data.request.process_stage || 'open', // Keep current process stage
        note: comment.trim(),
      });
      await fetchRequest();
      setComment('');
      showNotification('success', 'Comment added successfully');
    } catch (error) {
      console.error('Failed to add comment:', error);
      showNotification('error', 'Failed to add comment');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!data) return;

    setUpdating(true);
    try {
      await api.put(`/requests/${id}/status`, {
        process_stage: newStatus,
      });
      await fetchRequest();
      showNotification('success', 'Request status updated successfully');
    } catch (error) {
      console.error('Failed to update status:', error);
      showNotification('error', 'Failed to update request status');
    } finally {
      setUpdating(false);
    }
  };

  const handleTimestampUpdate = async (field: string, value: string | null) => {
    if (!data) return;

    setSaving(true);
    try {
      const updateData: any = {};
      updateData[field] = value;

      await api.put(`/requests/${id}`, updateData);
      await fetchRequest();
      showNotification('success', 'Timestamp updated successfully');
    } catch (error: any) {
      console.error('Failed to update timestamp:', error);
      showNotification('error', error?.response?.data?.error || 'Failed to update timestamp');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Request not found</h2>
          <Link to="/requests" className="text-cyan-600 hover:text-cyan-800 mt-4 inline-block">
            Back to requests
          </Link>
        </div>
      </div>
    );
  }

  const { request, updates } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const hasUnsavedGeneralChanges = data ? (
    reportingType !== (data.request.reportingType || '') ||
    zones !== (data.request.zones || '') ||
    leakageType !== (data.request.leakageType || '') ||
    materialUsed !== (data.request.materialUsed || '')
  ) : false;

  const hasUnsavedTechnicalChanges = data ? (
    technicalAllocation !== (data.request.technicalAllocation || '') ||
    damageType !== (data.request.damageType || '') ||
    repairMaterial !== (data.request.repairMaterial || '') ||
    pipeDiameter !== (data.request.pipeDiameter || '') ||
    pipeMaterial !== (data.request.pipeMaterial || '')
  ) : false;

  const hasUnsavedDamageChanges = data ? (
    supplyPressure !== (data.request.supplyPressure || '') ||
    holeSize !== (data.request.holeSize || '')
  ) : false;

  // Helper function to check if an update is a comment
  const isComment = (update: typeof updates[0]) => {
    const note = update.note.toLowerCase();
    return !note.startsWith('status changed to') &&
           !note.startsWith('updated description') &&
           !note.startsWith('updated reporting type') &&
           !note.startsWith('updated leakage') &&
           !note.startsWith('updated material') &&
           !note.startsWith('updated ') &&
           !note.startsWith('cleared ');
  };

  // Helper function to check if an update is a field change
  const isFieldChange = (update: typeof updates[0]) => {
    const note = update.note;
    return note.startsWith('Updated description') ||
           note.startsWith('Updated reporting type') ||
           note.startsWith('Updated leakage') ||
           note.startsWith('Updated material') ||
           note.startsWith('Cleared ') ||
           (note.startsWith('Updated ') && !note.startsWith('Status changed to')) ||
           note.startsWith('Status changed to');
  };

  const comments = updates.filter(isComment);
  const fieldChanges = updates.filter(isFieldChange);

  const displayedComments = comments.slice(0, commentsToShow);
  const displayedHistory = fieldChanges.slice(0, historyToShow);

  const hasMoreComments = comments.length > commentsToShow;
  const hasMoreHistory = fieldChanges.length > historyToShow;

  return (
    <div className="p-8">
      <Link
        to="/requests"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to requests
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">{request.ticketNumber}</h1>
          </div>
        </div>
      </div>

      {!data?.permissions.canEdit && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
          <p className="text-gray-700 font-medium">Read-only access</p>
          <p className="text-gray-600 text-sm">You don't have permission to edit this request. You can view details but cannot make changes.</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Description</h2>
              {!isEditingDescription && request.status !== 'completed' && data?.permissions.canEdit && (
                <button
                  onClick={() => setIsEditingDescription(true)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              )}
            </div>

            {isEditingDescription && request.status !== 'completed' ? (
              <div className="space-y-3">
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveDescription}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                  >
                    <Check className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelDescription}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`text-gray-700 leading-relaxed p-3 rounded-lg transition-colors ${
                  request.status !== 'completed' && data?.permissions.canEdit ? 'cursor-pointer hover:bg-gray-50' : ''
                }`}
                onClick={() => request.status !== 'completed' && data?.permissions.canEdit && setIsEditingDescription(true)}
              >
                {request.description}
              </div>
            )}
          </div>

          {/* General Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">General</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reporting Type
                </label>
                <JiraDropdown
                  options={reportingTypeOptions}
                  value={reportingType}
                  onChange={setReportingType}
                  placeholder="Select reporting type..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zones
                </label>
                <JiraDropdown
                  options={zonesOptions}
                  value={zones}
                  onChange={setZones}
                  placeholder="Select zone..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              {hasUnsavedGeneralChanges && request.status !== 'completed' && data?.permissions.canEdit && (
                <button
                  onClick={handleSaveDetails}
                  disabled={saving}
                  className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors w-full"
                >
                  {saving ? 'Saving...' : 'Save General Changes'}
                </button>
              )}
            </div>
          </div>

          {/* Technical Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Technical</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technical Allocation
                </label>
                <JiraDropdown
                  options={technicalAllocationOptions}
                  value={technicalAllocation}
                  onChange={setTechnicalAllocation}
                  placeholder="Select technical allocation..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Damage Type
                </label>
                <JiraDropdown
                  options={damageTypeOptions}
                  value={damageType}
                  onChange={setDamageType}
                  placeholder="Select damage type..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Repair Material
                </label>
                <JiraDropdown
                  options={repairMaterialOptions}
                  value={repairMaterial}
                  onChange={setRepairMaterial}
                  placeholder="Select repair material..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pipe Diameter
                </label>
                <JiraDropdown
                  options={pipeDiameterOptions}
                  value={pipeDiameter}
                  onChange={setPipeDiameter}
                  placeholder="Select pipe diameter..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pipe Material
                </label>
                <JiraDropdown
                  options={pipeMaterialOptions}
                  value={pipeMaterial}
                  onChange={setPipeMaterial}
                  placeholder="Select pipe material..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingConfigs}
                  loading={loadingConfigs}
                />
              </div>

              {hasUnsavedTechnicalChanges && request.status !== 'completed' && data?.permissions.canEdit && (
                <button
                  onClick={handleSaveDetails}
                  disabled={saving}
                  className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors w-full"
                >
                  {saving ? 'Saving...' : 'Save Technical Changes'}
                </button>
              )}
            </div>
          </div>

          {/* Damage Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Damage</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supply Pressure
                </label>
                <JiraDropdown
                  options={supplyPressureOptions}
                  value={supplyPressure}
                  onChange={setSupplyPressure}
                  placeholder="Select supply pressure..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingSupplyPressure}
                  loading={loadingSupplyPressure}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hole Size
                </label>
                <JiraDropdown
                  options={holeSizeOptions}
                  value={holeSize}
                  onChange={setHoleSize}
                  placeholder="Select hole size..."
                  disabled={!data?.permissions.canEdit || request.status === 'completed' || loadingHoleSize}
                  loading={loadingHoleSize}
                />
              </div>

              {hasUnsavedDamageChanges && request.status !== 'completed' && data?.permissions.canEdit && (
                <button
                  onClick={handleSaveDetails}
                  disabled={saving}
                  className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors w-full"
                >
                  {saving ? 'Saving...' : 'Save Damage Changes'}
                </button>
              )}
            </div>
          </div>

          {/* Add Comment */}
          {data?.permissions.canAddComment && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Comment</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                />
              </div>

              <button
                onClick={handleAddComment}
                disabled={updating || !comment.trim()}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {updating ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </div>
          )}

          {/* Activity */}
          {updates.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity</h2>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTab('comments')}
                    className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                      activeTab === 'comments'
                        ? 'border-cyan-600 text-cyan-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Comments
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      activeTab === 'comments'
                        ? 'bg-cyan-100 text-cyan-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {comments.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                      activeTab === 'history'
                        ? 'border-cyan-600 text-cyan-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    History
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      activeTab === 'history'
                        ? 'bg-cyan-100 text-cyan-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {fieldChanges.length}
                    </span>
                  </button>
                </div>
              </div>

              {/* Comments Tab */}
              {activeTab === 'comments' && (
                <div className="space-y-4">
                  {displayedComments.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No comments yet</p>
                  ) : (
                    <>
                      {displayedComments.map((update) => (
                        <div key={update.id} className="border-l-2 border-cyan-600 pl-4 py-2">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(update.status)}`}>
                                {formatStatus(update.status)}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">{formatDate(update.createdAt)}</span>
                          </div>
                          <p className="text-gray-700 mb-1">{update.note}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <UserCircle className="h-3 w-3" />
                            <span>{update.userName}</span>
                          </div>
                        </div>
                      ))}
                      {hasMoreComments && (
                        <button
                          onClick={() => setCommentsToShow(prev => prev + 5)}
                          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                        >
                          <ChevronDown className="h-4 w-4" />
                          Show more comments
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="space-y-4">
                  {displayedHistory.map((update) => {
                    const isStatusChange = update.note.startsWith('Status changed to');
                    const isUpdate = isFieldChange(update);

                    return (
                      <div key={update.id} className="border-l-2 border-cyan-600 pl-4 py-2">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(update.status)}`}>
                              {formatStatus(update.status)}
                            </span>
                            {isStatusChange && (
                              <span className="text-xs text-gray-500 italic">Status changed</span>
                            )}
                            {isUpdate && (
                              <span className="text-xs text-gray-500 italic">Field updated</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(update.createdAt)}</span>
                        </div>
                        <p className="text-gray-700 mb-1">{update.note}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <UserCircle className="h-3 w-3" />
                          <span>{update.userName}</span>
                        </div>
                      </div>
                    );
                  })}
                  {hasMoreHistory && (
                    <button
                      onClick={() => setHistoryToShow(prev => prev + 5)}
                      className="w-full flex items-center justify-center gap-2 py-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
                    >
                      <ChevronDown className="h-4 w-4" />
                      Show more activity
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Process Status Card - MOVED TO TOP */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Process Status</h2>

            {/* Current Status Badge */}
            <div className={`mb-4 p-3 rounded-lg border ${
              request.status === 'open' ? 'bg-blue-50 border-blue-200' :
              request.status === 'in_progress' ? 'bg-amber-50 border-amber-200' :
              request.status === 'pending' ? 'bg-orange-50 border-orange-200' :
              'bg-emerald-50 border-emerald-200'
            }`}>
              <p className="text-xs font-medium text-gray-500 mb-1">Main Status</p>
              <p className={`text-sm font-semibold ${
                request.status === 'open' ? 'text-blue-800' :
                request.status === 'in_progress' ? 'text-amber-800' :
                request.status === 'pending' ? 'text-orange-800' :
                'text-emerald-800'
              }`}>{
                request.status === 'open' ? 'Open' :
                request.status === 'in_progress' ? 'In Progress' :
                request.status === 'pending' ? 'Pending' :
                'Completed'
              }</p>
            </div>

            {/* Current Process Stage */}
            {request.process_stage && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-500">Current Stage</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{PROCESS_STAGE_LABELS[request.process_stage] || request.process_stage}</p>
              </div>
            )}

            {/* Change Status Dropdown */}
            <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">Select New Stage</label>
            <ProcessStageDropdown
              value={request.process_stage || null}
              onChange={handleStatusChange}
              disabled={updating}
            />
            {updating && <p className="text-xs text-blue-600 mt-2">Updating...</p>}
          </div>

          {/* Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="text-sm text-gray-900">{request.location}</p>
                </div>
              </div>

              {request.branchName && (
                <div className="flex items-start gap-3">
                  <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Branch</p>
                    <p className="text-sm text-gray-900">{request.branchName}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Created</p>
                  <p className="text-sm text-gray-900">{formatDate(request.createdAt)}</p>
                </div>
              </div>

              {request.technicianName && (
                <div className="flex items-start gap-3">
                  <UserCircle className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Assigned To</p>
                    <p className="text-sm text-gray-900">{request.technicianName}</p>
                  </div>
                </div>
              )}

              {request.responseTime && (
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Response Time</p>
                    <p className="text-sm text-gray-900">{formatDuration(request.responseTime)}</p>
                  </div>
                </div>
              )}

              {request.completionTime && (
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Completion Time</p>
                    <p className="text-sm text-gray-900">{formatDuration(request.completionTime)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>


          {/* Operational Times Card */}
          {data && (
            <OperationalTimesCard request={data.request} />
          )}
        </div>
      </div>
    </div>
  );
}
